import { getCurrentLocale, onTranslationsApplied, t, translateSubtree } from './localization.ts';
import { onSlideChange, setNavigationBlocked } from './slides.ts';
const PANEL_SELECTOR = '[data-slide-panel]';
const PANEL_DRAWER_SELECTOR = '[data-slide-panel-drawer]';
const PANEL_BACKDROP_SELECTOR = '[data-slide-panel-backdrop]';
const PANEL_CLOSE_SELECTOR = '[data-slide-panel-close]';
const PANEL_BODY_SELECTOR = '[data-slide-panel-body]';
const PANEL_TITLE_SELECTOR = '[data-slide-panel-title]';
const PANEL_TRIGGER_ATTR = 'data-panel-id';
const BLOCKED_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End']);

interface FocusTrap {
  activate(): void;
  deactivate(): void;
  refresh(): void;
}

function createFocusTrap(container: HTMLElement): FocusTrap {
  const previousFocusStack: (Element | null)[] = [];
  let focusable: Element[] = [];
  let first: Element | null = null;
  let last: Element | null = null;

  function updateFocusable(): void {
    focusable = Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((node) => !node.hasAttribute('hidden'));
    first = focusable[0] || null;
    last = focusable[focusable.length - 1] || null;
  }

  function handleFocus(event: FocusEvent): void {
    if (!container.contains(event.target as Node)) {
      (first as HTMLElement)?.focus({ preventScroll: true });
    }
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        (last as HTMLElement)?.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      (first as HTMLElement)?.focus();
    }
  }

  return {
    activate() {
      previousFocusStack.push(document.activeElement);
      updateFocusable();
      if (!focusable.length) {
        container.setAttribute('tabindex', '-1');
        container.focus({ preventScroll: true });
        return;
      }
      (first as HTMLElement)?.focus({ preventScroll: true });
      document.addEventListener('focus', handleFocus as EventListener, true);
      document.addEventListener('keydown', handleKeydown, true);
    },
    deactivate(): void {
      document.removeEventListener('focus', handleFocus as EventListener, true);
      document.removeEventListener('keydown', handleKeydown, true);
      const lastFocused = previousFocusStack.pop();
      if (lastFocused && 'focus' in lastFocused) {
        (lastFocused as HTMLElement).focus({ preventScroll: true });
      }
    },
    refresh() {
      updateFocusable();
    },
  };
}

const PERSONAL_NOTE_SLIDE_ID = 'personal-note';
const SCHEDULE_CALL_PANEL_ID = 'schedule-call';
const SCHEDULE_CALL_PRELOAD_LOCALE = 'et';
const SCHEDULE_CALL_IFRAME_SRC =
  'https://nt.neetocal.com/embed/a011c3b4-1ac9-4153-8192-d79f649f4e5d';
let scheduleCallPreloadTriggered: boolean = false;
let scheduleCallPreloadedIframe: HTMLIFrameElement | null = null;
let scheduleCallIframeStash: HTMLDivElement | null = null;

function ensureScheduleCallIframeStash(): HTMLDivElement {
  if (!scheduleCallIframeStash) {
    scheduleCallIframeStash = document.createElement('div');
    scheduleCallIframeStash.hidden = true;
    scheduleCallIframeStash.style.display = 'none';
    scheduleCallIframeStash.style.width = '0';
    scheduleCallIframeStash.style.height = '0';
    scheduleCallIframeStash.style.overflow = 'hidden';
  }
  if (!scheduleCallIframeStash.isConnected) {
    document.body.appendChild(scheduleCallIframeStash);
  }
  return scheduleCallIframeStash;
}

function ensureScheduleCallIframePreload(): HTMLIFrameElement {
  const stash = ensureScheduleCallIframeStash();
  if (scheduleCallPreloadedIframe) {
    if (!scheduleCallPreloadedIframe.isConnected) {
      stash.appendChild(scheduleCallPreloadedIframe);
    }
    return scheduleCallPreloadedIframe;
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('allowfullscreen', '');
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('title', 'Iframe embed');
  iframe.width = '100%';
  iframe.height = '717';
  iframe.src = SCHEDULE_CALL_IFRAME_SRC;
  iframe.dataset.scheduleCallEmbed = 'true';
  stash.appendChild(iframe);
  scheduleCallPreloadedIframe = iframe;
  return scheduleCallPreloadedIframe;
}

function preloadScheduleCallEmbedIfNeeded(index: number): void {
  if (scheduleCallPreloadTriggered) return;
  if (getCurrentLocale() !== SCHEDULE_CALL_PRELOAD_LOCALE) return;

  const slides = document.querySelectorAll('.slide');
  const currentSlide = slides[index];
  if (!currentSlide || currentSlide.id !== PERSONAL_NOTE_SLIDE_ID) return;

  scheduleCallPreloadTriggered = true;
  ensureScheduleCallIframePreload();
}

interface SlidePanelController {
  openPanel: (panelId: string, triggerLabel?: string) => void;
  closePanel: () => void;
}

function initSlidePanel(): SlidePanelController | undefined {
  const panel = document.querySelector(PANEL_SELECTOR);
  const drawer = panel?.querySelector(PANEL_DRAWER_SELECTOR);
  const backdrop = panel?.querySelector(PANEL_BACKDROP_SELECTOR);
  const closeButton = panel?.querySelector(PANEL_CLOSE_SELECTOR);
  const body = panel?.querySelector(PANEL_BODY_SELECTOR);
  const title = panel?.querySelector(PANEL_TITLE_SELECTOR);
  const templates = new Map<string, Map<string, HTMLTemplateElement>>();
  const DEFAULT_LOCALE_KEY = 'default';

  if (!panel || !drawer || !backdrop || !closeButton || !body || !title) {
    return;
  }

  document.querySelectorAll('template[data-panel-template]').forEach((template) => {
    const panelId = (template as HTMLElement).dataset.panelTemplate;
    if (!panelId) return;
    const localeKey = (template as HTMLElement).dataset.locale || DEFAULT_LOCALE_KEY;
    if (!templates.has(panelId)) {
      templates.set(panelId, new Map());
    }
    templates.get(panelId)!.set(localeKey, template as HTMLTemplateElement);
  });

  if (!templates.size) {
    return;
  }

  const focusTrap = createFocusTrap(drawer as HTMLElement);
  const docEl = document.documentElement;
  let isOpen: boolean = false;
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let pendingNavUnblock: boolean = false;
  let windowWheelSuppressTimeout: number | null = null;
  const wheelEventOptions: AddEventListenerOptions = { passive: false };

  const handleDrawerWheel = (event: WheelEvent): void => {
    event.preventDefault();
  };

  const handleDrawerKeydown = (event: KeyboardEvent): void => {
    if (BLOCKED_KEYS.has(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleWindowWheel = (event: WheelEvent): void => {
    if (isOpen || pendingNavUnblock) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  function ensureWindowWheelSuppressed(): void {
    if (windowWheelSuppressTimeout) {
      clearTimeout(windowWheelSuppressTimeout);
      windowWheelSuppressTimeout = null;
    }
    // Remove first to avoid duplicate listeners
    window.removeEventListener('wheel', handleWindowWheel, wheelEventOptions);
    window.addEventListener('wheel', handleWindowWheel, wheelEventOptions);
  }

  function updateCloseButtonLabel(): void {
    const closeLabel = t('slidePanels.close', '← Back');
    closeButton!.textContent = closeLabel;
    closeButton!.setAttribute('aria-label', closeLabel);
  }

  updateCloseButtonLabel();
  onTranslationsApplied(updateCloseButtonLabel);

  // Ensure wheel events are not suppressed on initialization
  window.removeEventListener('wheel', handleWindowWheel, wheelEventOptions);
  windowWheelSuppressTimeout = null;
  pendingNavUnblock = false;

  const retranslatePanelBody = (): void => {
    if (body.childElementCount) {
      translateSubtree(body);
    }
  };

  onTranslationsApplied(retranslatePanelBody);

  function closePanel(): void {
    if (!isOpen) return;
    panel!.classList.remove('is-visible');
    panel!.classList.remove('schedule-call-et');
    isOpen = false;
    pendingNavUnblock = true;
    setNavigationBlocked(false);
    drawer!.removeEventListener('wheel', handleDrawerWheel as EventListener);
    drawer!.removeEventListener('keydown', handleDrawerKeydown as EventListener, true);

    // Immediate wheel release for better responsiveness
    window.removeEventListener('wheel', handleWindowWheel, wheelEventOptions);
    if (windowWheelSuppressTimeout) {
      clearTimeout(windowWheelSuppressTimeout);
      windowWheelSuppressTimeout = null;
    }
    pendingNavUnblock = false;

    if (reduceMotionQuery.matches) {
      (panel as HTMLElement).hidden = true;
      panel!.removeAttribute('data-active-panel');
    }

    focusTrap.deactivate();
    document.body.classList.remove('slide-panel-open');
    docEl.classList.remove('slide-panel-open');
    if (scheduleCallPreloadedIframe && scheduleCallPreloadedIframe.isConnected) {
      const stash = ensureScheduleCallIframeStash();
      stash!.appendChild(scheduleCallPreloadedIframe);
    }
    body!.innerHTML = '';
  }

  function resolvePanelTemplate(panelId: string): HTMLTemplateElement | null {
    const bucket = templates.get(panelId);
    if (!bucket) return null;
    const locale = getCurrentLocale();
    if (locale && bucket.has(locale)) {
      return bucket.get(locale) || null;
    }
    if (bucket.has(DEFAULT_LOCALE_KEY)) {
      return bucket.get(DEFAULT_LOCALE_KEY) || null;
    }
    const iterator = bucket.values().next();
    return iterator.done ? null : iterator.value;
  }

  function openPanel(panelId: string, triggerLabel?: string): void {
    const template = resolvePanelTemplate(panelId);
    if (!template) {
      return;
    }
    body!.innerHTML = '';
    const fragment = template.content.cloneNode(true);
    body!.appendChild(fragment);
    translateSubtree(body!);
    body!.scrollTop = 0;
    drawer!.scrollTop = 0;
    focusTrap.refresh();

    if (triggerLabel) {
      title!.textContent = triggerLabel;
    } else {
      title!.textContent = panelId;
    }

    (panel as HTMLElement).dataset.activePanel = panelId;
    (panel as HTMLElement).hidden = false;
    requestAnimationFrame(() => {
      panel!.classList.add('is-visible');
      document.body.classList.add('slide-panel-open');
      docEl.classList.add('slide-panel-open');
      focusTrap.activate();
    });

    isOpen = true;
    setNavigationBlocked(true);
    ensureWindowWheelSuppressed();
    drawer!.addEventListener('wheel', handleDrawerWheel as EventListener, wheelEventOptions);
    drawer!.addEventListener('keydown', handleDrawerKeydown as EventListener, true);

    if (panelId === SCHEDULE_CALL_PANEL_ID && getCurrentLocale() === SCHEDULE_CALL_PRELOAD_LOCALE) {
      panel!.classList.add('schedule-call-et');
      const embedElement = body!.querySelector('[data-schedule-call-embed]');
      if (embedElement) {
        const preloaded = ensureScheduleCallIframePreload();
        if (preloaded !== embedElement) {
          embedElement.replaceWith(preloaded);
        }
      }
    } else {
      panel!.classList.remove('schedule-call-et');
    }
  }

  function handleTriggerClick(event: MouseEvent): void {
    const trigger = (event.target as HTMLElement)?.closest(`[${PANEL_TRIGGER_ATTR}]`);
    if (!trigger) return;
    const panelId = trigger.getAttribute(PANEL_TRIGGER_ATTR);
    if (!panelId) return;
    event.preventDefault();
    event.stopPropagation();
    const label = trigger.getAttribute('data-i18n')
      ? trigger.textContent.trim()
      : trigger.getAttribute('aria-label') || trigger.textContent.trim();
    openPanel(panelId, label);
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (!isOpen) return;
    if (event.key === 'Escape') {
      closePanel();
    }
  }

  document.addEventListener('click', handleTriggerClick);
  closeButton!.addEventListener('click', closePanel);
  backdrop.addEventListener('click', closePanel);
  document.addEventListener('keydown', handleKeydown);

  onSlideChange((payload) => {
    if (isOpen) {
      closePanel();
    }
    preloadScheduleCallEmbedIfNeeded(payload.index);
  });

  document.addEventListener('slide-navigation-blocked', (event: Event) => {
    if (!isOpen) return;
    const source = (event as CustomEvent)?.detail?.source;
    if (source === 'history' || source === 'generic') {
      closePanel();
    }
  });

  drawer!.addEventListener('transitionend', ((event: TransitionEvent) => {
    if (event.target !== drawer) return;
    if (event.propertyName !== 'transform') return;
    if (panel.classList.contains('is-visible')) return;
    if (isOpen) return;
    (panel as HTMLElement).hidden = true;
    panel!.removeAttribute('data-active-panel');
  }) as EventListener);

  return {
    openPanel,
    closePanel,
  };
}

export { initSlidePanel };
